import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OrgMembership, VehicleListItem } from 'types/api.generated';
import { listMyVehicles } from 'features/vehicles/services/vehicleApi';
import { isRateLimited } from 'core/api/rateLimit';
import { queryClient } from 'core/query/queryClient';
import { queryKeys } from 'core/query/queryKeys';

const MIN_REFRESH_MS = 5000;
let vehiclesRefreshPromise: Promise<VehicleListItem[]> | null = null;
let lastVehiclesRefreshAt = 0;

const ACTIVE_ORG_KEY = 'errandi_active_org_id';
const ACTIVE_VEHICLE_KEY = 'errandi_active_vehicle_id';

const readStored = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string | null): void => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

interface ProviderState {
  orgMemberships: OrgMembership[];
  activeOrgId: string | null;
  activeVehicleId: string | null;
  vehicles: VehicleListItem[];
  vehiclesLoading: boolean;
  setOrgMemberships: (memberships: OrgMembership[]) => void;
  setActiveOrgId: (orgId: string | null) => void;
  setActiveVehicleId: (vehicleId: string | null) => void;
  setVehicles: (vehicles: VehicleListItem[]) => void;
  updateVehicle: (vehicleId: string, patch: Partial<VehicleListItem>) => void;
  refreshVehicles: (options?: { force?: boolean }) => Promise<VehicleListItem[]>;
  reset: () => void;
}

export const useProviderStore = create<ProviderState>()(
  devtools(
    (set, get) => ({
      orgMemberships: [],
      activeOrgId: readStored(ACTIVE_ORG_KEY),
      activeVehicleId: readStored(ACTIVE_VEHICLE_KEY),
      vehicles: [],
      vehiclesLoading: false,

      setOrgMemberships: (memberships) => {
        const current = get().activeOrgId;
        const valid =
          current && memberships.some((m) => m.orgId === current)
            ? current
            : memberships[0]?.orgId ?? null;
        writeStored(ACTIVE_ORG_KEY, valid);
        set({ orgMemberships: memberships, activeOrgId: valid });
      },

      setActiveOrgId: (orgId) => {
        writeStored(ACTIVE_ORG_KEY, orgId);
        set({ activeOrgId: orgId });
      },

      setActiveVehicleId: (vehicleId) => {
        writeStored(ACTIVE_VEHICLE_KEY, vehicleId);
        set({ activeVehicleId: vehicleId });
      },

      setVehicles: (vehicles) => {
        const current = get().activeVehicleId;
        const valid =
          current && vehicles.some((v) => v.id === current)
            ? current
            : vehicles[0]?.id ?? null;
        writeStored(ACTIVE_VEHICLE_KEY, valid);
        set({ vehicles, activeVehicleId: valid });
      },

      updateVehicle: (vehicleId, patch) => {
        set({
          vehicles: get().vehicles.map((v) =>
            v.id === vehicleId ? { ...v, ...patch } : v,
          ),
        });
      },

      refreshVehicles: async (options) => {
        if (options?.force) {
          lastVehiclesRefreshAt = 0;
        }

        const existing = get().vehicles;
        if (
          !options?.force &&
          existing.length > 0 &&
          Date.now() - lastVehiclesRefreshAt < MIN_REFRESH_MS
        ) {
          return existing;
        }

        if (isRateLimited()) {
          return existing;
        }

        if (vehiclesRefreshPromise) {
          return vehiclesRefreshPromise;
        }

        set({ vehiclesLoading: true });
        vehiclesRefreshPromise = (async () => {
          try {
            const vehicles = await listMyVehicles();
            lastVehiclesRefreshAt = Date.now();
            get().setVehicles(vehicles);
            queryClient.setQueryData(queryKeys.vehicles.mine, vehicles);
            const orgId = get().activeOrgId;
            if (orgId) {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.organizations.assignable(orgId),
              });
            }
            return vehicles;
          } finally {
            set({ vehiclesLoading: false });
            vehiclesRefreshPromise = null;
          }
        })();

        return vehiclesRefreshPromise;
      },

      reset: () => {
        writeStored(ACTIVE_ORG_KEY, null);
        writeStored(ACTIVE_VEHICLE_KEY, null);
        set({
          orgMemberships: [],
          activeOrgId: null,
          activeVehicleId: null,
          vehicles: [],
          vehiclesLoading: false,
        });
      },
    }),
    { name: 'ProviderStore', enabled: import.meta.env.DEV },
  ),
);
